import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory } from 'react-router-dom';
import { IconButton, InputAdornment, Paper } from '@material-ui/core';
import moment from 'moment';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import TextField from '@material-ui/core/TextField';
import SearchIcon from '@material-ui/icons/Search';
import Button from '@material-ui/core/Button';

import NewPatientModal from '../components/NewPatientModal';

import { MENU_WIDTH } from '../constants';
import { getPatients,editPatient } from '../store/actions/patients';

const columns = [
  {
    id: 'name',
    label: 'Name',
    minWidth: 170,
    align: 'center',
  },
  {
    id: 'unit',
    label: 'Unit',
    minWidth: 100,
    align: 'center',
  },
  {
    id: 'room',
    label: 'Room Number',
    minWidth: 100,
    align: 'center',
  },
  {
    id: 'devices',
    label: 'Monitors',
    minWidth: 170,
    format: (value) => {
      const [a, b] = value;
      return (
        <div>
          <span>{a}</span>
          <br />
          <span>{b}</span>
        </div>
      );
    },
    align: 'center',
  },
];

const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
    paddingLeft: MENU_WIDTH,
    paddingTop: '6rem',
    height: '100%',
  },
  headerRow: {
    display: 'flex',
    flexDirection: 'row',
    marginLeft: '2rem',
    marginTop: '2rem',
    justifyContent: 'flex-end',
    marginRight: '2rem',
  },
  paper: {
    margin: '2rem',
  },
  content: {
    flexGrow: 1,
    borderRadius: 4,
  },
  table: {
    width: '100%',
  },
  headerCell: {
    backgroundColor: '#526488',
    fontWeight: 'bold',
    textAlign: 'center',
    color: 'white',
  },
  p: {
    margin: 0,
  },
  button: {
    color: 'white',
    backgroundColor: '#00d1b2',
    '&:hover': {
      backgroundColor: '#00bca0',
    },
  },
  searchBox: {
    minWidth: 350,
  },
}));

const Patients = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const patientsList = useSelector((state) => state.patients.patientsList);
  const patientCount = useSelector((state) => state.patients.patientCount);
  const [displayList, setDisplayList] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isNewPatientModalOpen, setIsNewPatientModalOpen] = useState(false);
  const classes = useStyles();

  useEffect(() => {
    dispatch(getPatients(false, page + 1, rowsPerPage));
  }, []);

  useEffect(() => {
    setDisplayList(
      patientsList
      .sort((a, b) => b.id - a.id) // Sort patients by id (descending)
      .map((patient) => ({
        id: patient.id,
        name: `${patient.last_name}, ${patient.first_name}`,
        dob: patient.dob,
        unit: patient.unit,
        room: patient.room,
        devices: [
          patient.device_a ? patient.device_a.mac_address : '',
          patient.device_b ? patient.device_b.mac_address : '',
        ],
      }))
    );
  }, [patientsList]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    dispatch(getPatients(false, newPage + 1, rowsPerPage));
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
    dispatch(getPatients(false, 1, +event.target.value));
  };

  const handleNavigation = (id) => {
    history.push(`./patients/${id}`);
  };

  return (
    <div className={classes.root}>
      <NewPatientModal open={isNewPatientModalOpen} setOpen={setIsNewPatientModalOpen} />
      <div className={classes.headerRow}>
        <Button className={classes.button} variant="contained" onClick={() => setIsNewPatientModalOpen(true)}>
          Add New Patient
        </Button>
      </div>
      <Paper className={classes.paper}>
        <TableContainer className={classes.content}>
          <Table stickyHeader aria-label="sticky table" className={classes.table}>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    className={classes.headerCell}
                    key={column.label}
                    align={column.align}
                    style={{ minWidth: column.minWidth }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {displayList.map((row) => {
                return (
                  <TableRow className={classes.row} hover key={row.id} onClick={() => handleNavigation(row.id)}>
                    {columns.map((column) => {
                      const value = row[column.id];
                      return (
                        <TableCell key={column.id} align={column.align} className={classes.cell}>
                          {column.format ? column.format(value) : value}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={patientCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Paper>
    </div>
  );
};

export default Patients;
